import type { DateVal, DurationObject } from "../../../utils";
import type { AmlAlerts, KycAlerts } from "../../profiles/types/alerts.type";
import type { AmlSearchesType } from "../../profiles/types/amlScreen.type";
import type { Attachment } from "../../../shared/types/attachment.type";
import type { AddressesPayload } from "../../profiles/types/address.type";
import type { CreateProfileType } from "../../profiles/types/createProfile.type";
import type { UboDetails } from "../../profiles/types/profile.type";
import type { Gender } from "../aml-utils/amlCheckUtils";

export interface SearchValues {
  countryCode: string;
  name: string;
  firstName: string;
  middleName: string;
  lastName: string;
  searchType: string;
  threshold: number;
  dateOfBirth: Date | null;
  categories: string[];
  gender: string;
  pepTiers: string[];
  countryCodes?: string[];
  isDobRequired?: boolean;
  sanctionBodies?: number[];
  dobMatching?: string;
  source?: string;
}

export interface AMLBusinessSearchRequest {
  type?: string;
  name: string;
  threshold: number;
  countryCode?: string[];
  source: string;
  datasets?: string[];
  categories?: string[];
  categoriesParam?: string[];
  pepTiersParam?: string[];
  countryCodes?: string[];
  keyPartyId?: string;
  sanctionBodies?: number[];
}

export interface AMLIndividualSearchRequest {
  type?: string;
  name?: string;
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  threshold: number;
  countryCodes: string[];
  source: string;
  dateOfBirth: string | null | DateVal;
  gender: Gender | null | undefined;
  datasets: string[];
  pepTiers: string[];
  keyPartyId?: string;
  isDobRequired?: boolean;
  sanctionBodies?: number[];
  dobMatching?: string;
}

export interface AmlIndividualSearchHit {
  id: string;
  hitScore: number;
  name: string;
  match: string;
  countries: string[];
  datasets: string[];
  decision: string;
  note: string | null;
  modifiedById: number;
  modifiedBy: string;
  modifiedAt: string;
  createdAt: string;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  gender: string;
  datesOfBirth: string[];
  pepTier: string | null;
  profileImages: string[] | null;
  isTrueMatch?: boolean;
  isFalsePositive?: boolean;
  showMatchDecisionIcons?: boolean;
  isClicked?: boolean;
  isDiscarded?: boolean;
  isChecked?: boolean;
}

export interface AmlBusinessSearchHits {
  id: string;
  hitScore: number;
  name: string;
  match: string;
  countries: string[] | null;
  datasets: string[] | null;
  decision: string;
  note: string | null;
  modifiedById: number;
  modifiedBy: string;
  modifiedAt: string;
  createdAt: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  gender?: string;
  datesOfBirth?: string[];
  pepTier?: string;
  profileImages?: string[];
  isTrueMatch?: boolean;
  isFalsePositive?: boolean;
  showMatchDecisionIcons?: boolean;
  isClicked?: boolean;
  isDiscarded?: boolean;
  isChecked?: boolean;
}

export interface AmlSearchResult {
  name: string;
  countries: string[];
  datasets: string[];
  match: string;
  id: string;
  hitScore: number;
  decision: string;
  note: string | null;
  createdAt: string;
  modifiedAt: string;
  modifiedById: number;
  modifiedBy: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  gender?: string;
  datesOfBirth?: string[];
  pepTier?: string;
  profileImages?: string[];
  isChecked?: boolean;
}

export interface AmlSearchResponse {
  items: AmlSearchResult[];
  count: number;
  totalSize?: number;
}

export interface AmlBusinessHitsResponse {
  items: AmlBusinessSearchHits[];
  count: number;
  totalSize?: number;
}

export interface ProfileAmlScreenSearchResponse {
  items: AmlSearchesType[];
  count: number;
  totalSize?: number;
}

export interface AmlIndividualHitsResponse {
  items: AmlIndividualSearchHit[];
  count: number;
  totalSize?: number;
}

export interface KeyPartiesResponse {
  items: string[];
  count: number;
  totalSize?: number;
}

export type AmlDetailsResponse = AmlDetails;

export interface AmlDetails {
  id?: string;
  hitScore?: number;
  name?: string;
  match?: string;
  note?: string;
  countries?: string[];
  datasets?: string[];
  profileImages?: string[];
  screeningNotes?: string[];
  version?: string;
  modifiedAt?: string;
  modifiedById?: number;
  modifiedBy?: string;
  createdAt?: string;
  decision?: string;
  identifiers?: TypeAndValue[];
  addresses?: Address[];
  contacts?: TypeAndValue[];
  businessLinks?: BusinessLink[];
  individualLinks?: IndividualLink[];
  sources?: Source[];
  firstName?: string;
  middleName?: string;
  lastName?: string;
  gender?: string;
  datesOfBirth?: string[];
  isDeceased?: boolean;
  datesOfDeath?: string[];
  businessTypes?: string[];
  description?: string;
  activities?: string[];
  pepTier?: string;
  aliases?: Alias[];
  amlResults?: AMLResultDetails;
}

export interface AmlCheckDetails {
  name?: string;
  gender?: string;
  datesOfBirth?: string[];
  countries?: string[];
}

export interface PostProfileCreationParams {
  profileType: CreateProfileType;
  profileId: string;
  addressPayload: AddressesPayload;
  attachments: Attachment[];
  searchId: string;
}

export interface TypeAndValue {
  type?: string;
  value?: string;
}

export interface Address {
  postalCode?: string;
  line1?: string;
  line2?: string;
  type?: string;
  county?: string;
  countryCode?: string;
  city?: string;
}

export interface BusinessLink {
  relationship?: string;
  ownershipPercentage?: number;
  datasets?: string[];
  name?: string;
  supplierHitId?: string;
  individualLinks?: IndividualLink[];
}

export interface IndividualLink {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  supplierHitId?: string;
  relationship?: string;
  ownershipPercentage?: number;
  datasets?: string[];
}

export interface Alias {
  type?: string;
  name?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
}

export interface AMLResultDetails {
  AM?: AdverseMedia[];
  ENF?: Enforcement[];
  INS?: InsolvencyRegister[];
  POI?: ProfileOfInterest[];
  SAN?: {
    CURRENT?: Sanctions[];
    FORMER?: Sanctions[];
  };
  DD?: DisqualifiedDirector[];
  PEP?: {
    CURRENT?: Pep[];
    FORMER?: Pep[];
    LINKED?: PepLinked[];
  };
  SOE?: StateOwnedEnterprise;
  PEPLINKED?: PepLinked[];
}

export interface Sanctions {
  events?: Event[];
  sources?: Source[];
  type?: string;
  value?: SanctionValue;
}

export interface Enforcement {
  events?: Event[];
  sources?: Source[];
  type?: string;
  value?: Value;
}

export interface InsolvencyRegister {
  events: Event[];
  sources: Source[];
  type: string;
  value: InsolvencyValue;
}

export interface StateOwnedEnterprise {
  events?: Event[];
  sources?: Source[];
  type?: string;
  value?: StateOwnedEnterpriseValue;
}

export interface DisqualifiedDirector {
  events?: Event[];
  sources?: Source[];
  type?: string;
  value?: DisqualifiedDirectorValue;
}

export interface Pep {
  events?: Event[];
  sources?: Source[];
  type?: string;
  value?: PepValue;
}

export interface PepLinked {
  events?: Event[];
  sources?: Source[];
  type?: string;
  value?: IndividualLink;
}

export interface ProfileOfInterest {
  events: Event[];
  sources: Source[];
  type: string;
  value: ProfileOfInterestValue;
}

export interface AdverseMedia {
  events?: Event[];
  sources?: Source[];
  type?: string;
  value?: Value;
}

export interface Source {
  id?: string;
  title?: string;
  description?: string;
  link?: string;
  dateAdded?: string;
  isCopyrighted?: boolean;
  datePublished?: string;
  documentLink?: string;
  datasets?: string[];
  credibility?: string;
  language?: string;
}

export type { DurationObject } from "../../../utils";

export interface Event {
  id?: string;
  type?: string;
  date?: string;
  amount?: number;
  currencyCode?: string;
  duration?: string | DurationObject;
  sources?: Source[];
}

export interface Value {
  category?: string;
  subCategory?: string;
}

export interface InsolvencyValue {
  insolvencyIdNumber?: string;
  type?: string;
  debtAmount?: string;
  startDate?: string;
  endDate?: string;
  caseReferenceNumbers?: string[];
  solicitor?: string;
  court?: {
    name?: string;
    petitioner?: string;
    hearingDate?: string;
    petitionPresentedDate?: string;
  };
  orderDate?: string;
  gazetteIssuedDate?: string;
  awardDate?: string;
}

export interface Position {
  name?: string;
  segment?: string;
  country?: string;
  fromDate?: string;
  toDate?: string;
}

export interface ProfileOfInterestValue {
  category?: string;
  positions?: Position[];
}

export interface SanctionValue {
  sanctionId?: string;
  issuingBody?: string;
  origin?: string;
  sanctionTypes?: string[];
  measures?: string[];
  regime?: string;
}

export interface DisqualifiedDirectorValue {
  referenceNumber?: string;
  fromDate?: string;
  toDate?: string;
  organisations?: string[];
  reason?: string;
}

export interface PepValue {
  country?: string;
  position?: string;
  fromDate?: string;
  toDate?: string;
  segment?: string;
}

export interface StateOwnedEnterpriseValue {
  type?: string;
  level?: string;
}

export interface AddAmlSearchResponse {
  id: string;
}

export interface RouteParams {
  searchId: string;
  values: SearchValues;
  pageNumber: number;
  profileId: string;
  type: string;
  tab: string;
  subTab: string;
  searchesCurrentPageNumber: number;
  hitsCurrentPageNumber: number;
  fromLocation: string;
}

export interface LinkAmlSearchesToProfilePayload {
  searchIds: string[];
}

export interface Item {
  keyPartyId: string;
  searchId: string;
}

export interface LinkKeyPartyUboAmlSearchesToProfilePayload {
  items: Item[];
}

export interface LinkAmlSearchesToProfileFailed {
  failedItem: string;
  error: {
    type: string;
    title: string;
    status: number;
    detail: string;
    instance: string;
    additionalProp1: string;
    additionalProp2: string;
    additionalProp3: string;
  };
}

export interface LinkAmlSearchesToProfileResponse {
  successful: string[];
  failed: LinkAmlSearchesToProfileFailed[];
}

export interface AmlAlertsResponse {
  items: AmlAlerts[];
  count: number;
  totalSize?: number;
}

export interface OpenAmlAlertsResponse {
  riskRating: string;
  status: string;
  type: string;
  internalId: string;
  assignedToId: number;
  assignedTo: string;
  safeNumber: string;
  formationDate: string;
  createdById: number;
  createdBy: string;
  kycApprovedAt: string;
  kycReviewOn: string;
  kycStatusUpdatedOn: string;
  kycComments: string;
  noteCount: number;
  attachmentCount: number;
  keyPartyCount: number;
  uboCount: number;
  openAlertCount: number;
  id: string;
  name: string | null;
  countries: string[] | null;
  match: string | null;
  datasets: string[];
  isTrueMatch?: boolean;
  isFalsePositive?: boolean;
  isClicked?: boolean;
  showMatchDecisionIcons?: boolean;
  hitScore: number;
  decision: string;
  note: string | null;
  modifiedById: number;
  modifiedBy: string;
  modifiedAt: string;
  createdAt: string;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  gender?: string;
  datesOfBirth?: string[];
  pepTier?: string | null;
  profileImages?: string[] | null;
}

export interface AmlScreenHitsUpdatePayload {
  items: {
    hitId: string | undefined;
    note: string | null;
    decision: string | undefined;
  }[];
}

export interface AmlScreenHitsResponse {
  items: (AmlBusinessSearchHits | AmlIndividualSearchHit)[];
  count: number;
  totalSize?: number;
}

export interface KycAlertsResponse {
  items: KycAlerts[];
  count: number;
  totalSize?: number;
}

export interface UboDetailsResponse {
  items: UboDetails[];
  count: number;
  totalSize?: number;
}

export interface AmlSearchQueryParamsContainer {
  searchId?: string;
  pageNum?: number;
  searchType?: string;
  countryCode?: string | null;
  source?: string | null;
  name?: string | null;
  threshold?: number | string | null;
  categoriesParam?: string;
  dateOfBirth?: string | Date | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  gender?: string | null;
  pepTiersParam?: string | null;
  categories?: string[];
  pepTiers?: string[];
  isSeparateNameInputs?: string;
  isDobRequired?: boolean;
}
export interface AmlScreenHitsUpdateKeys {
  hitId: string | undefined;
  note: string | null;
  decision: string | undefined;
}
export interface AmlAlertUpdatePayload {
  note: string | null;
  decision?: string;
}

export interface BusinessIndividualKeyPartyUboAMLSearchResponse {
  id: string;
  name: string;
  threshold: number;
  type: string;
  datasets: string[];
  status: string;
  riskRating: string;
  createdById: number;
  createdBy: string;
  createdAt: string;
  modifiedById: number;
  modifiedBy: string;
  modifiedAt: string;
  totalHitCount: number;
  truePositiveHitsCount: number;
  falsePositiveHitsCount: number;
  undecidedHitsCount: number;
  countryCodes?: string[];
  assignedToUserId?: number;
  assignedUser?: string;
  note?: string;
  scheduleId?: string;
  firstName: string;
  middleName: string;
  lastName: string;
  gender: string;
  dateOfBirth: Date | null;
  pepTiers: string[];
  sanctionBodies: number[];
}
export interface GetKeyPartySearchesQueryParams {
  page: number;
  type?: string;
  limit?: number;
  keyPartyTypes?: string[];
}

export interface AmlSearchBulkLinkRequest {
  items: AMLIndividualSearchRequest[] | AMLBusinessSearchRequest[];
}
export interface BulkScreeningStatus {
  id: string;
  startedAt: string;
  status: string;
  criteriaCount: string;
}

export interface BulkScreeningStatusResponse {
  items?: BulkScreeningStatus[];
  count?: number;
  totalSize?: number;
}

export interface ProfileKYCAlertById {
  id: string;
  profileId: string;
  safeNumber: string;
  companyId: string;
  previousValue: string;
  newValue: string;
  ruleName: string;
  eventAt: string;
  status: string;
  note: string;
  createdAt: string;
  createdById: number;
  createdBy: string;
  modifiedAt: string;
  modifiedById: number;
  modifiedBy: string;
}

export interface KYCAlertUpdateRequest {
  note: string | undefined;
  status: string | null;
}

export interface FormValuesType {
  profileName: string;
  addSearchTo: string;
}
/* eslint-disable no-unused-vars */

export enum DownloadReportTypes {
  business = "businesses",
  individual = "individuals",
}

export enum AsyncJobStatus {
  created = "created",
  processing = "processing",
  completed = "completed",
}

export interface AsyncJobResponse {
  id: string;
  status: AsyncJobStatus;
}

export enum AmlScreenMatchDecisions {
  TrueMatch = "trueMatch",
  FalsePositive = "falsePositive",
  Undecided = "undecided",
  Discarded = "discarded",
}
/* eslint-enable no-unused-vars */

export type stringNullUndefinedType = string | null | undefined;

export type numberNullUndefinedType = number | null | undefined;

export type Hit = AmlBusinessSearchHits | AmlIndividualSearchHit;

export type Decision =
  | AmlScreenMatchDecisions.TrueMatch
  | AmlScreenMatchDecisions.FalsePositive;

export interface MatchDecisionsType {
  isTrueMatch: boolean;
  isFalsePositive: boolean;
}

export interface AmlHitRecentChangeItem {
  changeDescription: string;
  changeType: string;
  field: string;
  modifiedDateTime: string;
}

export interface AmlHitRecentChangesResponse {
  items: AmlHitRecentChangeItem[];
  changesLimitExceeded?: boolean;
}

export interface AmlHitTableSelectionState {
  isAllSelected: boolean;
  isAnySelected: boolean;
}
